import { Button, Flex, Popup } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { memo, useCallback } from 'react';

import {
    SectionFilters,
    SectionReportName,
    SectionReportTypeLineItem,
    SectionReportTypeRequest,
    SectionVisibleColumns,
} from '../ReportEditAndCreatePopupComponents';
import { Loading } from './components/Loading/Loading';
import { subtitles } from './ReportCreatePopup.constants';
import {
    useCreateReportPopup,
    useReportCreatePopup,
    useReportCreatePopupConfig,
    useReportCreatePopupSteps,
} from './ReportCreatePopup.hooks';
import { messages } from './ReportCreatePopup.messages';
import { ActiveStep } from './ReportCreatePopup.types';

export const ReportCreatePopup = memo(() => {
    const { isOpen, handleClose } = useCreateReportPopup();
    const { reportConfig, handleChangeReportConfig } = useReportCreatePopupConfig();
    const { isCreating, isLoading, isValueSelected, handleCreateReport } = useReportCreatePopup(reportConfig);
    const { activeStep, isFirstStep, isLastStep, isErrorsDisplayed, handleGoToPrevStep, handleGoToNextStep } =
        useReportCreatePopupSteps(reportConfig, handleCreateReport);

    const handleToggle = useCallback(
        (open: boolean) => {
            !open && handleClose();
        },
        [handleClose]
    );

    if (!reportConfig) {
        return null;
    }

    return (
        <Popup size='medium' preventAutoClose open={isOpen} onToggle={handleToggle}>
            <Popup.Header
                title={messages.title}
                subTitle={subtitles[activeStep]}
                actions={
                    <Flex spacing='16'>
                        <Button
                            size='medium'
                            color='blue10'
                            disabled={isCreating || isLoading || isFirstStep}
                            onClick={handleGoToPrevStep}
                        >
                            {messages.buttonBack}
                        </Button>

                        <Button
                            size='large'
                            color='blue80'
                            disabled={isCreating || isLoading || !isValueSelected}
                            onClick={handleGoToNextStep}
                        >
                            {isLastStep ? messages.buttonCreate : messages.buttonNext}
                        </Button>
                    </Flex>
                }
            />

            <Popup.Body>
                {isLoading && <Loading />}

                {!isLoading &&
                    activeStep === ActiveStep.ReportType &&
                    reportConfig.reportType === domain.ReportType.Request && (
                        <SectionReportTypeRequest
                            reportConfig={reportConfig}
                            onReportConfigChange={handleChangeReportConfig}
                        />
                    )}

                {!isLoading &&
                    activeStep === ActiveStep.ReportType &&
                    reportConfig.reportType === domain.ReportType.LineItem && (
                        <SectionReportTypeLineItem
                            reportConfig={reportConfig}
                            onReportConfigChange={handleChangeReportConfig}
                        />
                    )}

                {!isLoading && activeStep === ActiveStep.Filters && (
                    <SectionFilters
                        reportConfig={reportConfig}
                        onReportConfigChange={handleChangeReportConfig}
                        newReport
                    />
                )}

                {!isLoading && activeStep === ActiveStep.VisibleColumns && (
                    <SectionVisibleColumns
                        reportConfig={reportConfig}
                        onReportConfigChange={handleChangeReportConfig}
                    />
                )}

                {!isLoading && activeStep === ActiveStep.ReportName && (
                    <SectionReportName
                        reportConfig={reportConfig}
                        onReportConfigChange={handleChangeReportConfig}
                        isErrorsDisplayed={isErrorsDisplayed}
                    />
                )}
            </Popup.Body>
        </Popup>
    );
});

ReportCreatePopup.displayName = 'ReportCreatePopup';
